import { capitalizeString } from '@/shared/utils/format';

import { api } from '../../_generated/api/assets-api.generated';

export type TLinksApiTags = 'Project' | 'Device' | 'Gateway' | 'Document';

export const linksApi = api.enhanceEndpoints({
  addTagTypes: [
    'Project',
    'Device',
    'DeviceConnectivityStatus',
    'Gateway',
    'Document',
  ],
  endpoints: {
    DeleteAssetLink: {
      invalidatesTags: (_, __, arg) => {
        const { sourceType, sourceId, destinationType, destinationId } = arg;

        const sourceTag = capitalizeString(sourceType) as TLinksApiTags;
        const destinationTag = capitalizeString(
          destinationType,
        ) as TLinksApiTags;

        return [
          { type: sourceTag, id: sourceId },
          { type: sourceTag, id: 'LIST' },
          { type: destinationTag, id: destinationId },
          { type: destinationTag, id: 'LIST' },
        ];
      },
    },
    LinkDocumentToAsset: {
      invalidatesTags: (_, __, arg) => {
        const { documentId, assetType, assetId } = arg;

        const destinationTag = capitalizeString(assetType) as TLinksApiTags;

        return [
          { type: destinationTag, id: assetId },
          { type: destinationTag, id: 'LIST' },
          { type: 'Document', id: documentId },
          { type: 'Document', id: 'LIST' },
        ];
      },
    },
    LinkLibraryToProject: {
      invalidatesTags: (_, __, arg) => [{ type: 'Project', id: arg.projectId }],
    },
    LinkDeviceToProject: {
      invalidatesTags: (_, __, args) => {
        const { deviceId, projectId } = args;
        return [
          { type: 'Device' as const, id: deviceId },
          { type: 'Device' as const, id: 'LIST' },
          { type: 'Project' as const, id: projectId },
          { type: 'Project' as const, id: 'LIST' },
        ];
      },
    },
    UpdateDeviceToProjectLink: {
      invalidatesTags: (_, __, args) => {
        const { deviceId, projectId } = args;
        return [
          { type: 'Device' as const, id: deviceId },
          { type: 'Device' as const, id: 'LIST' },
          { type: 'Project' as const, id: projectId },
          { type: 'Project' as const, id: 'LIST' },
        ];
      },
    },
    LinkDeviceToGateway: {
      invalidatesTags: (_, __, args) => {
        const { deviceId, gatewayId } = args;
        return [
          { type: 'Device' as const, id: deviceId },
          { type: 'DeviceConnectivityStatus' as const, id: deviceId },
          { type: 'Device' as const, id: 'LIST' },
          { type: 'Gateway' as const, id: gatewayId },
          { type: 'Gateway' as const, id: 'LIST' },
        ];
      },
    },
    DefineGatewayLinksToDevice: {
      invalidatesTags: (_, __, args) => {
        const { deviceId, input } = args;

        let gatewayIds: string[];
        if (Array.isArray(input)) {
          gatewayIds = [...input.map((link) => link.destinationId)];
        } else {
          gatewayIds = [...input.destinationId];
        }

        const gatewayTags = gatewayIds.map((id) => ({
          type: 'Gateway' as const,
          id,
        }));

        return [
          { type: 'Device' as const, id: deviceId },
          { type: 'DeviceConnectivityStatus' as const, id: deviceId },
          { type: 'Device' as const, id: 'LIST' },
          { type: 'Gateway' as const, id: 'LIST' },
          ...gatewayTags,
        ];
      },
    },
    ReadAssetLink: {
      providesTags: (_, __, arg) => {
        const { sourceType, sourceId, destinationType, destinationId } = arg;

        const sourceTag = capitalizeString(sourceType) as TLinksApiTags;
        const destinationTag = capitalizeString(
          destinationType,
        ) as TLinksApiTags;
        return [
          { type: sourceTag, id: sourceId },
          { type: destinationTag, id: destinationId },
        ];
      },
    },
  },
});

export const {
  useDeleteAssetLinkMutation,
  useLinkDocumentToAssetMutation,
  useLinkLibraryToProjectMutation,
  useLinkDeviceToProjectMutation,
  useLinkDeviceToGatewayMutation,
  useUpdateDeviceToProjectLinkMutation,
  useDefineGatewayLinksToDeviceMutation,
  useLazyReadAssetLinkQuery,
} = linksApi;
